import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteButton } from "../../components/delete_button";
import { SimpleGrid, useSimpleGrid } from "../../components/simple_grid";
import { useDrawer } from "../../components/use_drawer";
import { bindInputValue, useInfo } from "../../form_utils";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../services/api";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
    },
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("12px"),
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        ...FC.shorthands.gap("10px"),
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    mono: {
        fontFamily: "Consolas, monospace",
    },
});

const statusOptions = [
    { value: "active", label: "启用" },
    { value: "inactive", label: "停用" },
    { value: "retired", label: "退役" },
];

const selectedLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || value || "";

const normalizePayload = (source, info) => {
    const payload = { ...(source || {}), ...(info || {}) };
    delete payload.id;
    delete payload.createdAtUtc;
    delete payload.updatedAtUtc;
    Object.keys(payload).forEach((key) => {
        if (typeof payload[key] === "string") {
            payload[key] = payload[key].trim();
        }
        if (payload[key] === "") {
            payload[key] = null;
        }
    });
    return payload;
};

const listTenants = async (_client, filter) => {
    try {
        return await apiClient.getTenants(filter ? { search: filter } : {});
    } catch (error) {
        console.error("Failed to list tenants:", error);
        return [];
    }
};

const listAssetOwners = async (_client, filter) => {
    try {
        return await apiClient.getAssetOwners(filter ? { search: filter } : {});
    } catch (error) {
        console.error("Failed to list asset owners:", error);
        return [];
    }
};

const StatusDropdown = ({ value, onChange, options = statusOptions }) => (
    <FC.Dropdown
        selectedOptions={[value || options[0].value]}
        value={selectedLabel(options, value || options[0].value)}
        onOptionSelect={(_, data) => onChange(data.optionValue)}
    >
        {options.map((option) => (
            <FC.Option key={option.value} value={option.value}>
                {option.label}
            </FC.Option>
        ))}
    </FC.Dropdown>
);

const TenantForm = ({ item, onSaved, onDeleted }) => {
    const styles = useStyles();
    const showToast = useToast();
    const [current, setCurrent] = useState(item);
    const [detail, setDetail] = useState(null);
    const [assetOwners, setAssetOwners] = useState([]);
    const [selectedAssetOwnerId, setSelectedAssetOwnerId] = useState("");
    const { info, setInfo, updateInfo } = useInfo({});

    const loadDetail = useCallback(async (id) => {
        if (!id) {
            setDetail(null);
            return;
        }
        const tenant = await apiClient.getTenant(id);
        setDetail(tenant);
    }, []);

    useEffect(() => {
        setCurrent(item);
        setInfo({});
        loadDetail(item?.id);
    }, [item, loadDetail, setInfo]);

    useEffect(() => {
        apiClient.getAssetOwners({ status: "active" })
            .then((items) => setAssetOwners(items || []))
            .catch((error) => console.error("Failed to load asset owners:", error));
    }, []);

    const source = current || { status: "active", type: "service_provider" };
    const v = bindInputValue(source, info, setInfo);
    const statusValue = info.status ?? source.status ?? "active";
    // Reported by GET /api/tenants/{id}: true once accounts exist under the current prefix. The
    // field is disabled rather than left editable-then-rejected, because the refusal is permanent
    // and an administrator should see that before typing.
    const prefixIsFrozen = Boolean(detail?.accountPrefixLocked);

    const saveTenant = async () => {
        const payload = normalizePayload(source, { ...info, status: statusValue });
        let saved;
        if (current?.id) {
            saved = await apiClient.updateTenant(current.id, payload);
        } else {
            saved = await apiClient.createTenant(payload);
        }
        setCurrent(saved);
        setInfo({});
        await loadDetail(saved.id);
        showToast("租户已保存", "success");
        onSaved?.({ close: false, item: saved });
    };

    const disableTenant = async () => {
        await apiClient.deleteTenant(current.id);
        showToast("租户已停用", "success");
        onDeleted?.();
    };

    const grantAccess = async () => {
        if (!current?.id || !selectedAssetOwnerId) {
            return;
        }
        await apiClient.grantTenantAssetOwnerAccess(current.id, {
            assetOwnerId: Number(selectedAssetOwnerId),
            scopeType: "all_assets",
            status: "active",
        });
        setSelectedAssetOwnerId("");
        await loadDetail(current.id);
        showToast("业主授权已更新", "success");
    };

    const revokeAccess = async (assetOwnerId) => {
        await apiClient.revokeTenantAssetOwnerAccess(current.id, assetOwnerId);
        await loadDetail(current.id);
        showToast("业主授权已停用", "success");
    };

    const accesses = detail?.assetOwnerAccesses || [];

    return (
        <div className={styles.form}>
            <div className={styles.row}>
                <FC.Input contentBefore="租户编号" {...v("seq")} />
                <FC.Input contentBefore="租户名称" {...v("name")} />
            </div>
            <FC.Field
                hint={
                    prefixIsFrozen
                        ? `已有客户账号使用 ${source.accountPrefix}. 前缀，不能再修改——改动会让这些账号登录不进来。`
                        : "该租户下客户账号的前缀，例如填 clototics 则账号为 clototics.zgh。只能用小写字母、数字和连字符；一旦被账号使用即不可更改。"
                }
            >
                <FC.Input
                    contentBefore="账号前缀"
                    disabled={prefixIsFrozen}
                    placeholder="例如 clototics"
                    {...v("accountPrefix")}
                />
            </FC.Field>
            <div className={styles.row}>
                <FC.Input contentBefore="类型" {...v("type")} />
                <StatusDropdown value={statusValue} onChange={(status) => updateInfo({ status })} />
            </div>
            <div className={styles.row}>
                <FC.Input contentBefore="联系人" {...v("contactName")} />
                <FC.Input contentBefore="邮箱" {...v("contactEmail")} />
            </div>
            <FC.Input contentBefore="电话" {...v("contactPhone")} />
            <FC.Field label="备注">
                <FC.Textarea rows={3} resize="vertical" {...v("notes")} />
            </FC.Field>

            {current?.id && (
                <>
                    <FC.Divider>服务业主授权</FC.Divider>
                    <div className={styles.actions}>
                        <FC.Dropdown
                            style={{ minWidth: 260 }}
                            selectedOptions={selectedAssetOwnerId ? [selectedAssetOwnerId] : []}
                            value={
                                assetOwners.find((owner) => owner.id?.toString() === selectedAssetOwnerId)?.name ||
                                "选择业主"
                            }
                            onOptionSelect={(_, data) => setSelectedAssetOwnerId(data.optionValue)}
                        >
                            {assetOwners.map((owner) => (
                                <FC.Option key={owner.id} value={owner.id?.toString()}>
                                    {`${owner.seq} - ${owner.name}`}
                                </FC.Option>
                            ))}
                        </FC.Dropdown>
                        <FC.Button icon={<IC.LinkRegular />} onClick={grantAccess}>
                            授权
                        </FC.Button>
                    </div>
                    <div className={styles.form}>
                        {accesses.map((access) => (
                            <div key={access.id} className={styles.actions}>
                                <FC.Badge appearance="filled" color={access.status === "active" ? "success" : "subtle"}>
                                    {access.status}
                                </FC.Badge>
                                <span style={{ flex: 1 }}>
                                    {access.assetOwner?.seq} - {access.assetOwner?.name}
                                </span>
                                {access.status === "active" && (
                                    <FC.Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<IC.DismissRegular />}
                                        onClick={() => revokeAccess(access.assetOwnerId)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <FC.Divider />
            <div className={styles.actions}>
                {current?.id && <DeleteButton onClick={disableTenant} />}
                <FC.Button icon={<IC.SaveRegular />} appearance="primary" onClick={saveTenant}>
                    保存
                </FC.Button>
            </div>
        </div>
    );
};

const AssetOwnerForm = ({ item, onSaved, onDeleted }) => {
    const styles = useStyles();
    const showToast = useToast();
    const [current, setCurrent] = useState(item);
    const { info, setInfo, updateInfo } = useInfo({});

    useEffect(() => {
        setCurrent(item);
        setInfo({});
    }, [item, setInfo]);

    const source = current || { status: "active", type: "owner" };
    const v = bindInputValue(source, info, setInfo);
    const statusValue = info.status ?? source.status ?? "active";

    const saveOwner = async () => {
        const payload = normalizePayload(source, { ...info, status: statusValue });
        const saved = current?.id
            ? await apiClient.updateAssetOwner(current.id, payload)
            : await apiClient.createAssetOwner(payload);
        setCurrent(saved);
        setInfo({});
        showToast("业主已保存", "success");
        onSaved?.({ close: true, item: saved });
    };

    const disableOwner = async () => {
        await apiClient.deleteAssetOwner(current.id);
        showToast("业主已停用", "success");
        onDeleted?.();
    };

    return (
        <div className={styles.form}>
            <div className={styles.row}>
                <FC.Input contentBefore="业主编号" {...v("seq")} />
                <FC.Input contentBefore="业主名称" {...v("name")} />
            </div>
            <div className={styles.row}>
                <FC.Input contentBefore="类型" {...v("type")} />
                <StatusDropdown value={statusValue} onChange={(status) => updateInfo({ status })} />
            </div>
            <div className={styles.row}>
                <FC.Input contentBefore="联系人" {...v("contactName")} />
                <FC.Input contentBefore="邮箱" {...v("contactEmail")} />
            </div>
            <FC.Input contentBefore="电话" {...v("contactPhone")} />
            <FC.Field label="备注">
                <FC.Textarea rows={3} resize="vertical" {...v("notes")} />
            </FC.Field>
            <FC.Divider />
            <div className={styles.actions}>
                {current?.id && <DeleteButton onClick={disableOwner} />}
                <FC.Button icon={<IC.SaveRegular />} appearance="primary" onClick={saveOwner}>
                    保存
                </FC.Button>
            </div>
        </div>
    );
};

export const TenantManagement = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { items, SearchInput, refresh } = useSimpleGrid({
        listItems: listTenants,
        placeholder: "按租户编号、名称、联系人搜索...",
    });
    const { openDrawer, closeDrawer, drawer } = useDrawer({ style: { width: 720 } });

    const onSaved = useCallback(({ close } = {}) => {
        refresh();
        if (close) {
            closeDrawer();
        }
    }, [closeDrawer, refresh]);

    const columns = useMemo(() => [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => "租户编号",
            renderCell: (item) => <FC.TableCellLayout>{item.seq}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => "租户名称",
            renderCell: (item) => <FC.TableCellLayout>{item.name}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "status",
            renderHeaderCell: () => "状态",
            renderCell: (item) => <FC.TableCellLayout>{item.status}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "assetOwnerCount",
            renderHeaderCell: () => "授权业主",
            renderCell: (item) => <FC.TableCellLayout>{item.activeAssetOwnerCount || 0}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "deviceCount",
            renderHeaderCell: () => "设备",
            renderCell: (item) => <FC.TableCellLayout>{item.deviceCount || 0}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => "操作",
            renderCell: (item) => (
                <FC.TableCellLayout>
                    <FC.Tooltip content={`代管 ${item.name}`} relationship="label">
                        <FC.Button
                            appearance="subtle"
                            icon={<IC.SettingsRegular />}
                            onClick={() => navigate(`/system/tenants/${item.id}/manage`)}
                        />
                    </FC.Tooltip>
                    <FC.Button
                        appearance="subtle"
                        icon={<IC.MoreCircleFilled />}
                        onClick={() =>
                            openDrawer({
                                header: "租户详情",
                                body: (
                                    <TenantForm
                                        item={item}
                                        onSaved={onSaved}
                                        onDeleted={() => {
                                            refresh();
                                            closeDrawer();
                                        }}
                                    />
                                ),
                            })
                        }
                    />
                </FC.TableCellLayout>
            ),
        }),
    ], [closeDrawer, navigate, onSaved, openDrawer, refresh]);

    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <FC.Label>快速搜索</FC.Label>
                <SearchInput />
                <FC.Button
                    appearance="primary"
                    icon={<IC.AddRegular />}
                    onClick={() =>
                        openDrawer({
                            header: "创建租户",
                            body: <TenantForm item={null} onSaved={onSaved} />,
                        })
                    }
                >
                    创建租户
                </FC.Button>
            </div>
            <SimpleGrid items={items} columns={columns} />
            {drawer}
        </div>
    );
};

export const AssetOwnerManagement = () => {
    const styles = useStyles();
    const { items, SearchInput, refresh } = useSimpleGrid({
        listItems: listAssetOwners,
        placeholder: "按业主编号、名称、联系人搜索...",
    });
    const { openDrawer, closeDrawer, drawer } = useDrawer({ style: { width: 640 } });

    const onSaved = useCallback(({ close } = {}) => {
        refresh();
        if (close) {
            closeDrawer();
        }
    }, [closeDrawer, refresh]);

    const columns = useMemo(() => [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => "业主编号",
            renderCell: (item) => <FC.TableCellLayout>{item.seq}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => "业主名称",
            renderCell: (item) => <FC.TableCellLayout>{item.name}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "status",
            renderHeaderCell: () => "状态",
            renderCell: (item) => <FC.TableCellLayout>{item.status}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "fieldCount",
            renderHeaderCell: () => "风场",
            renderCell: (item) => <FC.TableCellLayout>{item.fieldCount || 0}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "tenantCount",
            renderHeaderCell: () => "服务商",
            renderCell: (item) => <FC.TableCellLayout>{item.activeTenantCount || 0}</FC.TableCellLayout>,
        }),
        FC.createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => "操作",
            renderCell: (item) => (
                <FC.TableCellLayout>
                    <FC.Button
                        appearance="subtle"
                        icon={<IC.MoreCircleFilled />}
                        onClick={() =>
                            openDrawer({
                                header: "业主详情",
                                body: (
                                    <AssetOwnerForm
                                        item={item}
                                        onSaved={onSaved}
                                        onDeleted={() => {
                                            refresh();
                                            closeDrawer();
                                        }}
                                    />
                                ),
                            })
                        }
                    />
                </FC.TableCellLayout>
            ),
        }),
    ], [closeDrawer, onSaved, openDrawer, refresh]);

    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <FC.Label>快速搜索</FC.Label>
                <SearchInput />
                <FC.Button
                    appearance="primary"
                    icon={<IC.AddRegular />}
                    onClick={() =>
                        openDrawer({
                            header: "创建业主",
                            body: <AssetOwnerForm item={null} onSaved={onSaved} />,
                        })
                    }
                >
                    创建业主
                </FC.Button>
            </div>
            <SimpleGrid items={items} columns={columns} />
            {drawer}
        </div>
    );
};
